import { defineStore } from 'pinia'
import { reactive, ref } from 'vue'
import i18n from '@/i18n'
import { i18nSyncService } from '@/services/I18nSyncService'
import { AXES, isValidAxisValue, type AppearanceKey } from '@/config/themes'
import { syncDayjsLocale } from '@/utils/locale'

export const useUIStore = defineStore('ui', () => {
    // Initialize from storage or default to 'tr'
    const storedLocale = localStorage.getItem('user-locale')
    const locale = ref<string>(storedLocale || 'tr')

    // Theme state
    const storedTheme = localStorage.getItem('user-theme')
    const theme = ref<string>(storedTheme || 'dark')

    // Appearance axes — TENANT-düzeyi tasarım kimliği (TAP'tan yönetilir); kullanıcı tercihi
    // DEĞİL. localStorage'a yazılmaz/okunmaz; boot (tenantBoot) tenant değerlerini uygular.
    const appearance = reactive<Record<AppearanceKey, string>>(
        Object.fromEntries(AXES.map((a) => [a.key, a.default])) as Record<AppearanceKey, string>,
    )

    // Sidebar state for mobile
    const isMobileSidebarOpen = ref(false)

    // Telefon kabuğu (≤768): alt sekme çubuğundaki "Menü" tam ekran sayfası.
    const isMobileMenuOpen = ref(false)
    // Header'daki global diyaloglar başka yerlerden de açılabilsin (mobil ana ekran
    // ızgarası, menü sayfasındaki arama): durum burada, diyaloglar AppHeader'da.
    const isCommandPaletteOpen = ref(false)
    const isEligibilityOpen = ref(false)

    /**
     * Sets the application locale.
     * Updates Pinia state, localStorage, and the i18n instance.
     * Triggers backend sync to fetch translations for the new language.
     */
    const setLocale = async (newLocale: string) => {
        // 1. Update State
        locale.value = newLocale

        // 2. Persist
        localStorage.setItem('user-locale', newLocale)

        // 3. Update i18n instance
        if (i18n.global.locale.value) {
            // @ts-ignore
            i18n.global.locale.value = newLocale
        } else {
            // @ts-ignore
            i18n.global.locale = newLocale
        }

        syncDayjsLocale(newLocale.split('-')[0] || 'tr')

        // Update HTML lang attribute for accessibility
        document.querySelector('html')?.setAttribute('lang', newLocale)

        // 4. Sync translations from backend for new language
        await i18nSyncService.sync(newLocale)
    }

    /**
     * Sets the application theme.
     * Updates Pinia state and localStorage.
     */
    const setTheme = (newTheme: string) => {
        theme.value = newTheme
        localStorage.setItem('user-theme', newTheme)

        // Apply theme to body
        if (newTheme === 'light') {
            document.body.classList.add('theme-light')
        } else {
            document.body.classList.remove('theme-light')
        }
    }

    /**
     * Initialize locale on app start
     */
    const initLocale = () => {
        setLocale(locale.value)
    }

    /**
     * Initialize theme on app start
     */
    const initTheme = () => {
        setTheme(theme.value)
    }

    /**
     * Set one appearance axis (accent/font/radius/density/surface). Stamps its data-*
     * attribute on <html> and persists it. Axes are independent — this touches only one.
     */
    const setAxis = (key: AppearanceKey, value: string) => {
        const axis = AXES.find((a) => a.key === key)
        if (!axis || !isValidAxisValue(axis, value)) return
        appearance[key] = value
        document.documentElement.setAttribute(axis.attr, value)
    }

    /** Apply all appearance axes on app start (defaults; tenantBoot overlays the tenant's). */
    const initAppearance = () => {
        for (const a of AXES) {
            document.documentElement.setAttribute(a.attr, appearance[a.key])
            localStorage.removeItem('appearance-' + a.key) // eski kullanıcı-override döneminin kalıntıları
        }
        localStorage.removeItem('user-theme') // mod da tenant-düzeyi artık
    }

    /**
     * Toggles the mobile sidebar visibility.
     */
    const toggleMobileSidebar = () => {
        isMobileSidebarOpen.value = !isMobileSidebarOpen.value
    }

    return {
        locale,
        theme,
        appearance,
        isMobileSidebarOpen,
        isMobileMenuOpen,
        isCommandPaletteOpen,
        isEligibilityOpen,
        setLocale,
        setTheme,
        setAxis,
        initLocale,
        initTheme,
        initAppearance,
        toggleMobileSidebar
    }
})
